import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ConfigProvider, Spin, notification, theme } from "antd";
import axios from "axios";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  IoArrowBackOutline,
  IoBeakerOutline,
  IoCloseCircleOutline,
  IoCubeOutline,
  IoDiceOutline,
  IoFlameOutline,
  IoGameControllerOutline,
  IoLayersOutline,
  IoMedalOutline,
  IoPodiumOutline,
  IoRefreshOutline,
  IoRemoveCircleOutline,
  IoSparklesOutline,
  IoStarOutline,
  IoStatsChartOutline,
  IoStopwatchOutline,
  IoSyncOutline,
  IoTimeOutline,
  IoTrophyOutline,
  IoTrendingUpOutline,
} from "react-icons/io5";
import { normalizeLanguage, translatePair } from "../../services/language";
import { setLanguage as setI18nLanguage, t as tKey } from "../../i18n";

import "./statsFilter.css";

const GAME_STATS_MODE_KEYS = ["ciencia", "cienzar", "sciencevsazar", "azar"];
const STATS_MODE_FILTER_STORAGE_KEY = "xima_stats_mode_filter";

const readStoredModeFilter = () => {
  try {
    const stored = window.sessionStorage.getItem(STATS_MODE_FILTER_STORAGE_KEY) || "";
    return GAME_STATS_MODE_KEYS.includes(stored) ? stored : "";
  } catch {
    return "";
  }
};

const storeModeFilter = (value) => {
  try {
    window.sessionStorage.setItem(STATS_MODE_FILTER_STORAGE_KEY, value || "");
  } catch {
    // Sin almacenamiento: el filtro solo dura mientras la página esté abierta.
  }
};

const formatDurationLabel = (totalSeconds) => {
  const safeSeconds = Math.max(0, Number(totalSeconds) || 0);
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const formatLongDurationLabel = (totalSeconds) => {
  const safeSeconds = Math.max(0, Number(totalSeconds) || 0);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(seconds).padStart(2, "0")}`;
  }

  return formatDurationLabel(safeSeconds);
};

const formatMetricValue = (value, decimals = 2) =>
  Number(Math.max(0, Number(value) || 0)).toFixed(decimals);

const formatCimaxScore = (value) => String(Math.round(Math.max(0, Number(value) || 0)));

const MODE_LABELS = {
  ciencia: "CIENCIA",
  cienzar: "CIENZAR",
  sciencevsazar: "CONTRAZAR",
  azar: "AZAR",
};

const Stats = () => {
  const navigate = useNavigate();
  const { language: outletLanguage = "es" } = useOutletContext() || {};
  const language = normalizeLanguage(outletLanguage);
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [modeFilter, setModeFilter] = useState(readStoredModeFilter);
  const t = useCallback((es, en) => translatePair(language, es, en), [language]);
  useEffect(() => {
    setI18nLanguage(language);
  }, [language]);

  // El servidor calcula todo con TODAS las partidas guardadas (o solo las del modo elegido).
  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get("/tournaments/stats/me", {
        params: modeFilter ? { mode: modeFilter } : {},
      });
      setPayload(response?.data?.stats || null);
    } catch (error) {
      notification.error({
        message: t("Error de estadisticas", "Stats Error"),
        description:
          error?.response?.data?.message ||
          error?.message ||
          t("No se pudieron cargar las estadisticas.", "Could not load stats."),
      });
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [modeFilter, t]);

  useEffect(() => {
    loadStats().catch((error) => console.log(error));
  }, [loadStats]);

  const selectModeFilter = useCallback((nextMode) => {
    const safeMode = GAME_STATS_MODE_KEYS.includes(nextMode) ? nextMode : "";
    setModeFilter((current) => {
      const resolved = current === safeMode ? "" : safeMode;
      storeModeFilter(resolved);
      return resolved;
    });
  }, []);

  const stats = useMemo(() => {
    const summary = payload?.summary || {};
    const favoriteMode = modeFilter || payload?.favoriteMode || "";
    return {
      ...summary,
      favoriteModeLabel: MODE_LABELS[favoriteMode] || "-",
      cimaxScores: payload?.cimaxScores || {},
      byMode: payload?.byMode || {},
    };
  }, [payload, modeFilter]);

  const tournamentWinsByMode = useMemo(() => {
    const source = payload?.tournamentWinsByMode || {};
    const result = GAME_STATS_MODE_KEYS.reduce((accumulator, modeKey) => {
      accumulator[modeKey] = Math.max(0, Number(source?.[modeKey]) || 0);
      return accumulator;
    }, {});
    result.total = GAME_STATS_MODE_KEYS.reduce((sum, modeKey) => sum + result[modeKey], 0);
    return result;
  }, [payload]);

  const cards = useMemo(
    () => [
      {
        key: "matches",
        label: t("Partidas", "Matches"),
        value: Number(stats?.totalMatches || 0),
        className: "border-[#25508c] bg-[#0d2344]",
        labelClass: "text-[#8cc6ff]",
      },
      {
        key: "wins",
        label: t("Victorias", "Wins"),
        value: Number(stats?.wins || 0),
        className: "border-[#2b8b4e] bg-[#13372a]",
        labelClass: "text-[#86ffb5]",
      },
      {
        key: "losses",
        label: t("Derrotas", "Losses"),
        value: Number(stats?.losses || 0),
        className: "border-[#8b2b2b] bg-[#3b1a1a]",
        labelClass: "text-[#ff9f9f]",
      },
      {
        key: "draws",
        label: t("Sin ganador", "No winner"),
        value: Number(stats?.draws || 0),
        className: "border-[#5d6b80] bg-[#1c2430]",
        labelClass: "text-[#c6d2e4]",
      },
      {
        key: "avg",
        label: t("Tiempo medio", "Avg duration"),
        value: formatDurationLabel(stats?.averageDurationSeconds || 0),
        className: "border-[#5c4ab6] bg-[#1f2350]",
        labelClass: "text-[#bab3ff]",
      },
      {
        key: "totalTime",
        label: t("Tiempo total jugado", "Total play time"),
        value: formatLongDurationLabel(stats?.totalTimePlayedSeconds || 0),
        className: "border-[#336b7a] bg-[#112f3a]",
        labelClass: "text-[#9cecff]",
      },
      {
        key: "fav",
        label: t("Modo favorito", "Favorite mode"),
        value: stats?.favoriteModeLabel || "-",
        extra: `${t("Win rate", "Win rate")}: ${Number(stats?.winRate || 0).toFixed(1)}%`,
        className: "border-[#7a5a1f] bg-[#3a2a12]",
        labelClass: "text-[#ffe09a]",
      },
      {
        key: "currentStreak",
        label: t("Racha de victorias", "Win streak"),
        value: Number(stats?.currentWinStreak || 0),
        className: "border-[#2f8b76] bg-[#14392f]",
        labelClass: "text-[#9fffd8]",
      },
      {
        key: "bestStreak",
        label: t("Mejor racha", "Best streak"),
        value: Number(stats?.bestWinStreak || 0),
        className: "border-[#4f7d2f] bg-[#293a14]",
        labelClass: "text-[#cfff9f]",
      },
      {
        key: "capturedBlocks",
        label: t("Bloques capturados", "Captured blocks"),
        value: Number(stats?.capturedBlocks || 0),
        className: "border-[#8b5a2b] bg-[#3b2613]",
        labelClass: "text-[#ffc99f]",
      },
      {
        key: "capturedSpecial",
        label: t("Especiales capturadas", "Special captures"),
        value: Number(stats?.specialCaptures || 0),
        className: "border-[#7a2b8b] bg-[#35113e]",
        labelClass: "text-[#f4b0ff]",
      },
      {
        key: "dailyStreak",
        label: t("Racha de dias", "Day streak"),
        value: Number(stats?.consecutiveDaysStreak || 0),
        extra: t("Dias seguidos jugando", "Consecutive days playing"),
        className: "border-[#2b5f8b] bg-[#112a3f]",
        labelClass: "text-[#9fd7ff]",
      },
    ],
    [stats, t]
  );

  const tournamentWinsCards = useMemo(
    () => [
      {
        key: "science",
        label: t("Torneos ganados Ciencia", "Science tournaments won"),
        value: Number(tournamentWinsByMode?.ciencia || 0),
        className: "border-[#3c76c7] bg-[#102a4d]",
        labelClass: "text-[#bfe1ff]",
      },
      {
        key: "cienzar",
        label: t("Torneos ganados Cienzar", "Cienzar tournaments won"),
        value: Number(tournamentWinsByMode?.cienzar || 0),
        className: "border-[#2f9658] bg-[#123a27]",
        labelClass: "text-[#bfffd7]",
      },
      {
        key: "sciencevsazar",
        label: t("Torneos ganados Contrazar", "Contrazar tournaments won"),
        value: Number(tournamentWinsByMode?.sciencevsazar || 0),
        className: "border-[#b9414c] bg-[#3d1018]",
        labelClass: "text-[#ffc2ca]",
      },
      {
        key: "dice",
        label: t("Torneos ganados Azar", "Dice tournaments won"),
        value: Number(tournamentWinsByMode?.azar || 0),
        className: "border-[#b98a1f] bg-[#3f2d11]",
        labelClass: "text-[#ffe3a6]",
      },
    ],
    [tournamentWinsByMode, t]
  );

  const modeColumns = useMemo(() => {
    const modeStatsByApi = stats?.byMode || {};
    return [
      {
        key: "ciencia",
        title: t("Modo Ciencia", "Science mode"),
        cimaxLabel: t("Puntuacion CIMAX Ciencia", "CIMAX Science score"),
        cimaxScore: formatCimaxScore(stats?.cimaxScores?.ciencia || 0),
        wrapperClass: "border-[#3c76c7] bg-[#0f2646]",
        titleClass: "text-[#bfe1ff]",
        metricLabelClass: "text-[#93c9ff]",
      },
      {
        key: "cienzar",
        title: t("Modo Cienzar", "Cienzar mode"),
        cimaxLabel: t("Puntuacion CIMAX Cienzar", "CIMAX Cienzar score"),
        cimaxScore: formatCimaxScore(stats?.cimaxScores?.cienzar || 0),
        wrapperClass: "border-[#2f9658] bg-[#123926]",
        titleClass: "text-[#b8ffd4]",
        metricLabelClass: "text-[#88e8b1]",
      },
      {
        key: "sciencevsazar",
        title: t("Modo Contrazar", "Contrazar mode"),
        cimaxLabel: t("Puntuacion CIMAX Contrazar", "CIMAX Contrazar score"),
        cimaxScore: formatCimaxScore(
          stats?.cimaxScores?.sciencevsazar ||
            stats?.cimaxScores?.contrazar ||
            stats?.cimaxScores?.cimaxContrazar ||
            0
        ),
        wrapperClass: "border-[#b9414c] bg-[#3d1018]",
        titleClass: "text-[#ffc2ca]",
        metricLabelClass: "text-[#ff9ba7]",
      },
      {
        key: "azar",
        title: t("Modo Azar", "Dice mode"),
        cimaxLabel: t("Puntuacion CIMAX Azar", "CIMAX Dice score"),
        cimaxScore: formatCimaxScore(stats?.cimaxScores?.azar || 0),
        wrapperClass: "border-[#b98a1f] bg-[#3d2c10]",
        titleClass: "text-[#ffe2a1]",
        metricLabelClass: "text-[#ffd17a]",
      },
    ].map((column) => {
      const modeStats = modeStatsByApi?.[column.key]?.gameStats || {};
      return {
        ...column,
        metrics: [
          {
            key: "maxForce",
            label: t("Fuerza de juego maxima", "Max game force"),
            value: formatMetricValue(modeStats?.maxGameForceAchieved || 0),
          },
          {
            key: "maxLevel",
            label: t("Nivel maximo de un juego", "Max game level"),
            value: formatMetricValue(modeStats?.maxGameLevelAchieved || 0),
          },
          {
            key: "maxPotency",
            label: t("Potencia maxima", "Max potency"),
            value: formatMetricValue(modeStats?.maxGamePotency || 0),
          },
          {
            key: "avgForce",
            label: t("Fuerza de juego promedio", "Average game force"),
            value: formatMetricValue(modeStats?.averageGameForce || 0),
          },
          {
            key: "avgLevel",
            label: t("Nivel de juego promedio", "Average game level"),
            value: formatMetricValue(modeStats?.averageGameLevel || 0),
          },
          {
            key: "avgPotency",
            label: t("Potencia media", "Average potency"),
            value: formatMetricValue(modeStats?.averageGamePotency || 0),
          },
        ],
      };
    });
  }, [stats, t]);

  const topCimaxBadges = useMemo(
    () => [
      {
        key: "ciencia",
        label: t("CIMAX CIENCIA", "CIMAX SCIENCE"),
        value: formatCimaxScore(stats?.cimaxScores?.ciencia || 0),
        className: "border-[#3c76c7] bg-[#102a4d] text-[#bfe1ff]",
      },
      {
        key: "cienzar",
        label: t("CIMAX CIENZAR", "CIMAX CIENZAR"),
        value: formatCimaxScore(stats?.cimaxScores?.cienzar || 0),
        className: "border-[#2f9658] bg-[#123a27] text-[#bfffd7]",
      },
      {
        key: "sciencevsazar",
        label: t("CIMAX CONTRAZAR", "CIMAX CONTRAZAR"),
        value: formatCimaxScore(
          stats?.cimaxScores?.sciencevsazar ||
            stats?.cimaxScores?.contrazar ||
            stats?.cimaxScores?.cimaxContrazar ||
            0
        ),
        className: "border-[#b9414c] bg-[#3d1018] text-[#ffc2ca]",
      },
      {
        key: "azar",
        label: t("CIMAX AZAR", "CIMAX RANDOM"),
        value: formatCimaxScore(stats?.cimaxScores?.azar || 0),
        className: "border-[#b98a1f] bg-[#3f2d11] text-[#ffe3a6]",
      },
    ],
    [stats, t]
  );

  const cardsByKey = useMemo(
    () =>
      cards.reduce((accumulator, card) => {
        accumulator[card.key] = card;
        return accumulator;
      }, {}),
    [cards]
  );

  const statsTotals = useMemo(() => {
    const totalMatches = Math.max(0, Number(stats?.totalMatches) || 0);
    const wins = Math.max(0, Number(stats?.wins) || 0);
    const losses = Math.max(0, Number(stats?.losses) || 0);
    const draws = Math.max(0, Number(stats?.draws) || 0);
    const percentOf = (value) => (totalMatches > 0 ? (value / totalMatches) * 100 : 0);
    const winPercent = percentOf(wins);
    const lossPercent = percentOf(losses);
    const drawPercent = percentOf(draws);
    return {
      totalMatches,
      wins,
      losses,
      draws,
      winPercent,
      lossPercent,
      drawPercent,
      donutStyle: {
        "--xima-stats-win-angle": `${Math.min(100, winPercent) * 3.6}deg`,
        "--xima-stats-loss-end-angle": `${Math.min(100, winPercent + lossPercent) * 3.6}deg`,
      },
    };
  }, [stats]);

  const topCimaxPanels = useMemo(
    () =>
      topCimaxBadges.map((badge) => {
        const modeMeta = {
          ciencia: {
            tone: "is-ciencia",
            icon: IoCubeOutline,
            eyebrow: t("Tu CIMAX actual", "Your current CIMAX"),
          },
          cienzar: {
            tone: "is-cienzar",
            icon: IoBeakerOutline,
            eyebrow: "CIMAX CIENZAR",
          },
          sciencevsazar: {
            tone: "is-sciencevsazar",
            icon: IoSyncOutline,
            eyebrow: "CIMAX CONTRAZAR",
          },
          azar: {
            tone: "is-azar",
            icon: IoDiceOutline,
            eyebrow: "CIMAX AZAR",
          },
        }[badge.key] || {
          tone: "is-ciencia",
          icon: IoCubeOutline,
          eyebrow: badge.label,
        };
        return {
          ...badge,
          tone: modeMeta.tone,
          Icon: modeMeta.icon,
          eyebrow: modeMeta.eyebrow,
        };
      }),
    [topCimaxBadges, t]
  );

  const topMetricPanels = useMemo(
    () => [
      { ...cardsByKey.matches, tone: "is-blue", Icon: IoGameControllerOutline },
      { ...cardsByKey.wins, tone: "is-green", Icon: IoTrophyOutline },
      { ...cardsByKey.losses, tone: "is-red", Icon: IoCloseCircleOutline },
      { ...cardsByKey.draws, tone: "is-slate", Icon: IoRemoveCircleOutline },
      { ...cardsByKey.avg, tone: "is-purple", Icon: IoTimeOutline },
      { ...cardsByKey.totalTime, tone: "is-cyan", Icon: IoStopwatchOutline },
    ],
    [cardsByKey]
  );

  const accentMetricPanels = useMemo(
    () => [
      { ...cardsByKey.fav, tone: "is-gold", Icon: IoStarOutline },
      { ...cardsByKey.currentStreak, tone: "is-teal", Icon: IoTrendingUpOutline },
      { ...cardsByKey.bestStreak, tone: "is-green", Icon: IoMedalOutline },
      { ...cardsByKey.capturedBlocks, tone: "is-orange", Icon: IoLayersOutline },
      { ...cardsByKey.capturedSpecial, tone: "is-violet", Icon: IoSparklesOutline },
    ],
    [cardsByKey]
  );

  const summaryRows = useMemo(
    () => [
      { key: "currentStreak", label: cardsByKey.currentStreak?.label, value: cardsByKey.currentStreak?.value, Icon: IoTrendingUpOutline, tone: "is-green" },
      { key: "bestStreak", label: cardsByKey.bestStreak?.label, value: cardsByKey.bestStreak?.value, Icon: IoMedalOutline, tone: "is-green" },
      { key: "capturedBlocks", label: cardsByKey.capturedBlocks?.label, value: cardsByKey.capturedBlocks?.value, Icon: IoLayersOutline, tone: "is-orange" },
      { key: "capturedSpecial", label: cardsByKey.capturedSpecial?.label, value: cardsByKey.capturedSpecial?.value, Icon: IoSparklesOutline, tone: "is-violet" },
      { key: "avg", label: cardsByKey.avg?.label, value: cardsByKey.avg?.value, Icon: IoTimeOutline, tone: "is-purple" },
      { key: "totalTime", label: cardsByKey.totalTime?.label, value: cardsByKey.totalTime?.value, Icon: IoStopwatchOutline, tone: "is-cyan" },
    ],
    [cardsByKey]
  );

  const modePerformancePanels = useMemo(
    () =>
      modeColumns.map((column) => {
        const modeMeta = {
          ciencia: { tone: "is-ciencia", Icon: IoCubeOutline },
          cienzar: { tone: "is-cienzar", Icon: IoBeakerOutline },
          sciencevsazar: { tone: "is-sciencevsazar", Icon: IoSyncOutline },
          azar: { tone: "is-azar", Icon: IoDiceOutline },
        }[column.key] || { tone: "is-ciencia", Icon: IoCubeOutline };
        const modeSummary = stats?.byMode?.[column.key] || {};
        const modeRate = {
          wins: Number(modeSummary.wins || 0),
          losses: Number(modeSummary.losses || 0),
          draws: Number(modeSummary.draws || 0),
          matches: Number(modeSummary.totalMatches || 0),
        };
        return {
          ...column,
          ...modeMeta,
          rate: modeRate,
          tournamentWins:
            column.key === "ciencia"
              ? Number(tournamentWinsByMode?.ciencia || 0)
              : column.key === "cienzar"
              ? Number(tournamentWinsByMode?.cienzar || 0)
              : column.key === "sciencevsazar"
              ? Number(tournamentWinsByMode?.sciencevsazar || 0)
              : Number(tournamentWinsByMode?.azar || 0),
        };
      }),
    [modeColumns, stats, tournamentWinsByMode]
  );

  return (
    <ConfigProvider
      theme={{
        token: { colorPrimary: "rgb(90, 140, 255)" },
        algorithm: theme.darkAlgorithm,
      }}
    >
      <main className="mobile-landscape-safari-flex-parent xima-stats-page">
        <section className="mobile-landscape-safari-flex-child xima-stats-shell">
          <header className="xima-stats-header">
            <div className="xima-stats-heading">
              <div className="xima-stats-heading-icon">
                <IoStatsChartOutline aria-hidden="true" />
              </div>
              <div>
                <h1>{tKey("ESTADISTICAS")}</h1>
                <p>
                  {modeFilter
                    ? t(
                        `Solo partidas del modo ${MODE_LABELS[modeFilter]}, desde que te registraste.`,
                        `Only ${MODE_LABELS[modeFilter]} mode games, since you signed up.`
                      )
                    : t(
                        "Todas tus partidas desde que te registraste, en todos los modos.",
                        "All your games since you signed up, in every mode."
                      )}
                </p>
              </div>
            </div>
            <div className="xima-stats-header-actions">
              <button type="button" onClick={() => navigate("/game/history")}>
                <IoArrowBackOutline aria-hidden="true" />
                {t("Ir a Historial", "Go to History")}
                <IoSyncOutline aria-hidden="true" />
              </button>
              <button type="button" onClick={() => loadStats()} className="is-purple">
                <IoRefreshOutline aria-hidden="true" />
                {tKey("ACTUALIZAR")}
              </button>
            </div>
          </header>

          <nav className="xima-stats-mode-filter" aria-label={t("Filtrar por modo", "Filter by mode")}>
            {[{ key: "", label: t("Todos", "All") }, ...GAME_STATS_MODE_KEYS.map((key) => ({ key, label: MODE_LABELS[key] }))].map(
              (option) => (
                <button
                  key={option.key || "all"}
                  type="button"
                  className={`is-${option.key || "all"}${modeFilter === option.key ? " is-active" : ""}`}
                  aria-pressed={modeFilter === option.key}
                  onClick={() => selectModeFilter(option.key)}
                >
                  {option.label}
                  {option.key && payload?.byMode?.[option.key] ? (
                    <small>{Number(payload.byMode[option.key].totalMatches || 0)}</small>
                  ) : null}
                  {!option.key && payload ? <small>{Number(payload.allModesTotalMatches || 0)}</small> : null}
                </button>
              )
            )}
          </nav>

          {loading ? (
            <div className="xima-stats-loading">
              <Spin size="large" />
            </div>
          ) : (
            <div className="xima-stats-content">
              <section className="xima-stats-cimax-grid">
                {topCimaxPanels.map((panel) => {
                  const Icon = panel.Icon;
                  return (
                    <article
                      key={panel.key}
                      className={`xima-stats-cimax-card ${panel.tone} is-clickable${
                        modeFilter === panel.key ? " is-selected" : ""
                      }`}
                      role="button"
                      tabIndex={0}
                      aria-pressed={modeFilter === panel.key}
                      title={
                        modeFilter === panel.key
                          ? t("Volver a todos los modos", "Back to all modes")
                          : t("Ver solo las partidas de este modo", "Show only this mode's games")
                      }
                      onClick={() => selectModeFilter(panel.key)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          selectModeFilter(panel.key);
                        }
                      }}
                    >
                      <div className="xima-stats-icon-box">
                        <Icon aria-hidden="true" />
                      </div>
                      <div>
                        <span>{panel.eyebrow}</span>
                        <em>{panel.label}</em>
                        <strong>{panel.value}</strong>
                      </div>
                    </article>
                  );
                })}
                <article className="xima-stats-cimax-card is-purple">
                  <div className="xima-stats-icon-box">
                    <IoFlameOutline aria-hidden="true" />
                  </div>
                  <div>
                    <span>{cardsByKey.dailyStreak?.label}</span>
                    <strong>{cardsByKey.dailyStreak?.value}</strong>
                    <small>{cardsByKey.dailyStreak?.extra}</small>
                  </div>
                </article>
              </section>

              <section className="xima-stats-kpi-strip has-draws">
                {topMetricPanels.map((panel) => {
                  const Icon = panel.Icon;
                  return (
                    <article key={panel.key} className={`xima-stats-kpi-card ${panel.tone}`}>
                      <div className="xima-stats-kpi-icon">
                        <Icon aria-hidden="true" />
                      </div>
                      <div>
                        <span>{panel.label}</span>
                        <strong>{panel.value}</strong>
                      </div>
                    </article>
                  );
                })}
              </section>

              <section className="xima-stats-accent-grid">
                {accentMetricPanels.map((panel) => {
                  const Icon = panel.Icon;
                  return (
                    <article key={panel.key} className={`xima-stats-accent-card ${panel.tone}`}>
                      <div>
                        <span>{panel.label}</span>
                        <strong>{panel.value}</strong>
                        {panel.extra ? <small>{panel.extra}</small> : null}
                      </div>
                      <Icon aria-hidden="true" />
                    </article>
                  );
                })}
              </section>

              <div className="xima-stats-main-grid">
                <section className="xima-stats-performance-panel">
                  <div className="xima-stats-section-title">
                    <h2>{t("Rendimiento por modo de juego", "Performance by game mode")}</h2>
                  </div>

                  <div className="xima-stats-mode-grid">
                    {modePerformancePanels.map((panel) => {
                      const Icon = panel.Icon;
                      return (
                        <article
                          key={panel.key}
                          className={`xima-stats-mode-card ${panel.tone} is-clickable${
                            modeFilter === panel.key ? " is-selected" : modeFilter ? " is-dimmed" : ""
                          }`}
                          role="button"
                          tabIndex={0}
                          aria-pressed={modeFilter === panel.key}
                          title={
                            modeFilter === panel.key
                              ? t("Volver a todos los modos", "Back to all modes")
                              : t("Ver solo este modo", "Show only this mode")
                          }
                          onClick={() => selectModeFilter(panel.key)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              selectModeFilter(panel.key);
                            }
                          }}
                        >
                          <header>
                            <span>
                              <Icon aria-hidden="true" />
                            </span>
                            <h3>{panel.title}</h3>
                          </header>
                          <div className="xima-stats-mode-score">
                            <span>{panel.cimaxLabel}</span>
                            <strong>{panel.cimaxScore}</strong>
                          </div>
                          <dl className="xima-stats-metric-list">
                            {panel.metrics.map((metric) => (
                              <div key={metric.key}>
                                <dt>{metric.label}</dt>
                                <dd>{metric.value}</dd>
                              </div>
                            ))}
                          </dl>
                          <div className="xima-stats-mode-footer">
                            <div
                              className="xima-stats-ring"
                              style={{
                                "--xima-stats-ring-angle": `${
                                  (tournamentWinsByMode.total > 0
                                    ? Number(panel.tournamentWins || 0) / tournamentWinsByMode.total
                                    : 0) * 360
                                }deg`,
                              }}
                            >
                              <span>{panel.tournamentWins}</span>
                            </div>
                            <div>
                              <span>{t("Torneos ganados", "Tournaments won")}</span>
                              <strong>{panel.tournamentWins}</strong>
                              <small>
                                W {Number(panel.rate?.wins || 0)} · L{" "}
                                {Number(panel.rate?.losses || 0)} · D{" "}
                                {Number(panel.rate?.draws || 0)} · M{" "}
                                {Number(panel.rate?.matches || 0)}
                              </small>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </section>

                <aside className="xima-stats-side-column">
                  <section className="xima-stats-side-panel">
                    <h2>{t("Distribucion general", "Overall distribution")}</h2>
                    <div className="xima-stats-distribution">
                      <div className="xima-stats-donut has-draws" style={statsTotals.donutStyle}>
                        <div>
                          <strong>{statsTotals.totalMatches}</strong>
                          <span>{t("Partidas", "Matches")}</span>
                        </div>
                      </div>
                      <div className="xima-stats-distribution-copy">
                        <div className="is-win">
                          <span />
                          <strong>{t("Victorias", "Wins")}</strong>
                          <em>
                            {statsTotals.wins} ({statsTotals.winPercent.toFixed(1)}%)
                          </em>
                        </div>
                        <div className="is-loss">
                          <span />
                          <strong>{t("Derrotas", "Losses")}</strong>
                          <em>
                            {statsTotals.losses} ({statsTotals.lossPercent.toFixed(1)}%)
                          </em>
                        </div>
                        <div className="is-draw">
                          <span />
                          <strong>{t("Sin ganador", "No winner")}</strong>
                          <em>
                            {statsTotals.draws} ({statsTotals.drawPercent.toFixed(1)}%)
                          </em>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="xima-stats-side-panel">
                    <h2>{t("Resumen general", "General summary")}</h2>
                    <div className="xima-stats-summary-list">
                      {summaryRows.map((row) => {
                        const Icon = row.Icon;
                        return (
                          <div key={row.key} className={row.tone}>
                            <Icon aria-hidden="true" />
                            <span>{row.label}</span>
                            <strong>{row.value}</strong>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                </aside>
              </div>

              <section className="xima-stats-tournament-band">
                <div>
                  <h2>{t("Torneos ganados por modo", "Tournaments won by mode")}</h2>
                  <div className="xima-stats-tournament-grid">
                    {tournamentWinsCards.map((card) => (
                      <article key={card.key}>
                        <span>{card.label}</span>
                        <strong>{card.value}</strong>
                      </article>
                    ))}
                  </div>
                </div>
                <div className="xima-stats-total-tournaments">
                  <IoPodiumOutline aria-hidden="true" />
                  <span>{t("Total torneos ganados", "Total tournaments won")}</span>
                  <strong>{Number(tournamentWinsByMode?.total || 0)}</strong>
                </div>
              </section>
            </div>
          )}
        </section>
      </main>
    </ConfigProvider>
  );

};

export default Stats;
